import { ArrowLeft, Star } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/TextArea";
import { cn } from "@/lib/utils";
import Image from "../../user-authentication/component/Image";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import {
  useGetUserTicketDetailQuery,
  useGiveFeedbackMutation,
} from "@/services/LiveQueueService";
import { default as defaultImg } from "@/assets/default.jpg";
import { Separator } from "@/components/ui/Separator";
import { displayDate } from "@/utils/Helper";

interface FeedbackFormData {
  ticket_details: any;
  rating: number;
  waitTime: boolean;
  notes?: string;
}

const { VITE_VIEW_S3_URL } = import.meta.env;
import { toast } from "@/components/ui/UseToast";
import React from "react";
import { messaging } from "../../../../utils/lib/Firebase";
import { Messaging, onMessage } from "firebase/messaging";

export default function FeedbackForm() {
  const { id } = useParams();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [submitFeedback] = useGiveFeedbackMutation();
  const navigate = useNavigate();
  const { data } = useGetUserTicketDetailQuery({
    ticketId: id ? parseInt(id, 10) : 0,
    userId: userInfo?.id ? parseInt(userInfo.id, 10) : 0,
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    register,
    reset,
    formState: { errors },
  } = useForm<FeedbackFormData>({
    defaultValues: {
      rating: 0,
      waitTime: false,
      notes: "",
    },
  });
  
React.useEffect(() => {
  if (data) {
    reset({
      rating: data?.ticket_details.ticket_feedback.rating ?? 0,
      waitTime:
        data?.ticket_details.ticket_feedback.wait_for_long_time ?? false,
      notes: data?.ticket_details.ticket_feedback.feedback || "",
    });
  }
}, [data, reset]);

  const currentRating = watch("rating");

  const onSubmit = async (formData: FeedbackFormData) => {
    try {
      await submitFeedback({
        ticketId: id ? parseInt(id, 10) : 0,
        rating: formData.rating,
        waitTime: formData.waitTime,
        notes: formData.notes || "",
      }).unwrap();
      navigate(
        `/user/userticketdetail/${id}/${data?.ticket_details?.ticket_number}/${data?.ticket_details.service.department_id}/${data?.ticket_details.service.department_id}`
      );
       toast({
         title: "Feedback Submitted",
         description: "Ticket Submitted Successfully",
         variant: "success",
       });
    } catch (error) {
       toast({
         title: "Feedback Submission Error",
         description: "Error Submitting Feedback",
         variant: "success",
       });
    }
  };
 React.useEffect(() => {
     let unsubscribe: (() => void) | null = null;

     messaging().then((msgInstance: Messaging | null) => {
       if (!msgInstance) {
         console.error("Firebase Messaging is not initialized.");
         return;
       }

       unsubscribe = onMessage(msgInstance, (payload) => {
         if (payload.notification) {
           toast({
             variant: "default",
             description: payload.notification.body,
             title: payload.notification.title,
           });
           
         }
         const { data } = payload;
         if (data?.type && data?.id) {
          
         }
       });
     });

     return () => {
       if (unsubscribe) unsubscribe();
     };
   }, []);
  return (
    <div className="min-h-screen bg-white p-4 md:flex md:items-center md:justify-center">
      <div className="max-w-md w-full mx-auto">
        {/* Header */}
        <div className="flex flex-row items-center justify-evenly">
          <button
            onClick={() =>
              navigate(
                `/user/userticketdetail/${id}/${data?.ticket_details?.ticket_number}/${data?.ticket_details.service.department_id}/${data?.ticket_details.service.department_id}`
              )
            }
            className="w-10 h-10 rounded-full bg-[#FDF8EE] flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 text-[#C4A661]" />
          </button>
          <div className="w-full flex pl-[6.75rem]">
            <h1 className="text-2xl font-semibold ml-4">Feedback</h1>
          </div>
          <div></div>
        </div>
        <Separator />

        {/* Logo and Session Info */}
        <div className="text-center mt-4">
          <div className="mb-4 flex justify-center items-center">
            <Image
              src={
                data?.ticket_details.service.service_logo
                  ? `${VITE_VIEW_S3_URL + data?.ticket_details.service.service_logo}`
                  : defaultImg
              }
              alt={data?.ticket_details.service.name || "Service Name"}
              style={{ width: 120, height: 120 }}
              className="mx-auto"
            />
          </div>
          <div className="space-y-2">
            <div className="text-[#C4A661]">
              {new Date(`${data?.ticket_details.created_at ?? ""}Z`)
                .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")}| {data?.ticket_details.ticket_number}
            </div>
            <span className="inline-block px-4 py-1 bg-green-500 text-white rounded-full text-sm">
              Served
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-4">
          {/* Rating Section */}
          <div className="bg-[#FDF8EE] p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-2">
              How Was Your Experience?
            </h2>
            <p className="text-gray-500 mb-4">Your rating helps us improve</p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() =>
                    setValue("rating", star, { shouldValidate: true })
                  }
                  className="focus:outline-none"
                >
                  <Star
                    className={cn(
                      "w-8 h-8",
                      star <= currentRating
                        ? "fill-[#C4A661] text-[#C4A661]"
                        : "text-[#C4A661]"
                    )}
                  />
                </button>
              ))}
            </div>
            <input
              type="hidden"
              {...register("rating", {
                required: "Rating is required",
                min: { value: 1, message: "Minimum 1 rating must be selected" },
              })}
            />
            {errors.rating && (
              <p className="text-red-500 text-sm">{errors.rating.message}</p>
            )}
          </div>

          {/* Wait Time Section */}
          <div className="bg-[#FDF8EE] p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4">
              Did You Wait For A Long Time?
            </h2>
            <Controller
              name="waitTime"
              control={control}
              rules={{
                validate: (value) =>
                  typeof value === "boolean" ? true : "Please select an option",
              }}
              render={({ field }) => (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ].map((option) => (
                    <label key={option.label} className="relative">
                      <input
                        type="radio"
                        onChange={() => {
                          field.onChange(option.value);
                        }}
                        checked={field.value === option.value}
                        className="sr-only peer"
                      />
                      <div className="w-full p-3 text-center bg-[#C4A661] bg-opacity-50 rounded-lg peer-checked:bg-opacity-100 peer-checked:text-white cursor-pointer">
                        {option.label}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            />
            {errors.waitTime && (
              <p className="text-red-500 text-sm">{errors.waitTime.message}</p>
            )}
          </div>

          {/* Notes Section */}
          <div className="bg-[#FDF8EE] p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4">Notes</h2>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  className="w-full bg-white border-0 resize-none h-32"
                  placeholder="Write your notes here..."
                  maxLength={350}
                />
              )}
            />
            {errors.notes && (
              <p className="text-red-500 text-sm">{errors.notes.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-[#8B1D1D] hover:bg-[#7A1919] text-white py-6 text-lg"
          >
            Submit
          </Button>
        </form>
      </div>
    </div>
  );
}
